import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

function euro(v) { return Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }

const TYP_LABEL = { fixkosten: 'Fixkosten', einnahme: 'Einnahme', variabel: 'Variable Ausgabe' };
const TYP_COLOR = { fixkosten: '#ef4444', einnahme: '#10b981', variabel: '#f59e0b' };
const INTERVALL_LABEL = {
  monthly: 'Monatlich', quarterly: 'Vierteljährlich', halfyear: 'Halbjährlich',
  yearly: 'Jährlich', weekly: 'Wöchentlich', biweekly: 'Zweiwöchentlich'
};
const CONF_COLOR = { HIGH: '#10b981', MEDIUM: '#f59e0b', LOW: '#94a3b8' };

function ConfBadge({ c }) {
  return <span style={{
    background: CONF_COLOR[c] + '22', color: CONF_COLOR[c],
    fontWeight: 700, fontSize: 10, padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap'
  }}>{c}</span>;
}

export default function WiederkehrendPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [msg, setMsg] = useState('');
  const [filter, setFilter] = useState('all'); // all|unbestaetigt|bestaetigt
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newForm, setNewForm] = useState({
    bezeichnung: '', empfaenger: '', kategorie: '', typ: 'fixkosten',
    intervall: 'monthly', erwarteter_betrag: '', naechstes_datum: ''
  });
  const [kategorien, setKategorien] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'unbestaetigt' ? { bestaetigt: 0 }
        : filter === 'bestaetigt' ? { bestaetigt: 1 } : {};
      const [r, u, k] = await Promise.all([
        api.get('/recurring', { params }),
        api.get('/recurring/upcoming', { params: { days: 30 } }),
        api.get('/kategorien')
      ]);
      setItems(r.data.items || []);
      setUpcoming(u.data.items || []);
      setKategorien(k.data || []);
    } catch (e) {
      setMsg('Fehler beim Laden: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function detect() {
    setDetecting(true);
    setMsg('');
    try {
      const r = await api.post('/recurring/detect');
      const d = r.data;
      setMsg(`Erkennung abgeschlossen: ${d.candidates} Kandidaten, ${d.inserted} neu, ${d.updated} aktualisiert, ${d.skipped_confirmed} bestätigt übersprungen.`);
      await load();
    } catch (e) {
      setMsg('Fehler: ' + (e.response?.data?.error || e.message));
    } finally { setDetecting(false); }
  }

  async function confirm(item) {
    await api.put(`/recurring/${item.id}`, { bestaetigt: 1 });
    await load();
  }

  async function ignore(item) {
    if (!window.confirm(`"${item.bezeichnung}" ignorieren (deaktivieren)?`)) return;
    await api.put(`/recurring/${item.id}`, { aktiv: 0 });
    await load();
  }

  async function del(item) {
    if (!window.confirm(`"${item.bezeichnung}" löschen?`)) return;
    await api.delete(`/recurring/${item.id}`);
    await load();
  }

  function startEdit(item) {
    setEditId(item.id);
    setEditData({
      bezeichnung: item.bezeichnung || '',
      empfaenger: item.empfaenger || '',
      kategorie: item.kategorie || '',
      typ: item.typ || 'fixkosten',
      intervall: item.intervall || 'monthly',
      erwarteter_betrag: item.erwarteter_betrag != null ? String(item.erwarteter_betrag) : '',
      naechstes_datum: item.naechstes_datum || ''
    });
  }

  async function saveEdit() {
    const r = editData;
    await api.put(`/recurring/${editId}`, {
      bezeichnung: r.bezeichnung,
      empfaenger: r.empfaenger,
      kategorie: r.kategorie,
      typ: r.typ,
      intervall: r.intervall,
      erwarteter_betrag: parseFloat(r.erwarteter_betrag),
      naechstes_datum: r.naechstes_datum || null
    });
    setEditId(null);
    await load();
  }

  async function addManual(e) {
    e.preventDefault();
    try {
      await api.post('/recurring', {
        ...newForm,
        erwarteter_betrag: parseFloat(newForm.erwarteter_betrag)
      });
      setShowAdd(false);
      setNewForm({ bezeichnung: '', empfaenger: '', kategorie: '', typ: 'fixkosten', intervall: 'monthly', erwarteter_betrag: '', naechstes_datum: '' });
      await load();
    } catch (ex) {
      setMsg('Fehler: ' + (ex.response?.data?.error || ex.message));
    }
  }

  const inp = { border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px', fontSize: 12, width: '100%', boxSizing: 'border-box' };
  const btn = (bg) => ({ background: bg, color: 'white', border: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700 });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 20, color: '#1e293b' }}>Wiederkehrende Zahlungen</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowAdd(v => !v)} style={btn('#6366f1')}>+ Manuell</button>
          <button onClick={detect} disabled={detecting} style={btn(detecting ? '#94a3b8' : '#0a6f4f')}>
            {detecting ? 'Erkenne...' : 'Erkennen'}
          </button>
        </div>
      </div>

      {msg && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
          {msg}
        </div>
      )}

      {upcoming.length > 0 && (
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 8 }}>Demnächst (30 Tage)</div>
          {upcoming.slice(0, 5).map((u, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < upcoming.slice(0,5).length-1 ? '1px solid #f1f5f9' : 'none', fontSize: 13 }}>
              <span style={{ color: '#64748b' }}>{u.datum}</span>
              <span style={{ flex: 1, marginLeft: 12, color: '#1e293b' }}>{u.bezeichnung}</span>
              <span style={{ color: u.betrag < 0 ? '#ef4444' : '#10b981', fontWeight: 700 }}>
                {u.isVariable ? 'ca. ' : ''}{euro(u.betrag)}
              </span>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <form onSubmit={addManual} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Neue Serie manuell anlegen</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div><label style={{ fontSize: 11, color: '#64748b' }}>Bezeichnung *</label><input style={inp} value={newForm.bezeichnung} onChange={e => setNewForm({...newForm, bezeichnung: e.target.value})} required /></div>
            <div><label style={{ fontSize: 11, color: '#64748b' }}>Empfänger</label><input style={inp} value={newForm.empfaenger} onChange={e => setNewForm({...newForm, empfaenger: e.target.value})} /></div>
            <div><label style={{ fontSize: 11, color: '#64748b' }}>Typ *</label>
              <select style={inp} value={newForm.typ} onChange={e => setNewForm({...newForm, typ: e.target.value})}>
                {Object.entries(TYP_LABEL).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11, color: '#64748b' }}>Intervall *</label>
              <select style={inp} value={newForm.intervall} onChange={e => setNewForm({...newForm, intervall: e.target.value})}>
                {Object.entries(INTERVALL_LABEL).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11, color: '#64748b' }}>Betrag (neg.=Ausgabe) *</label><input style={inp} type="number" step="0.01" value={newForm.erwarteter_betrag} onChange={e => setNewForm({...newForm, erwarteter_betrag: e.target.value})} required /></div>
            <div><label style={{ fontSize: 11, color: '#64748b' }}>Nächstes Datum</label><input style={inp} type="date" value={newForm.naechstes_datum} onChange={e => setNewForm({...newForm, naechstes_datum: e.target.value})} /></div>
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button type="submit" style={btn('#0a6f4f')}>Speichern</button>
            <button type="button" onClick={() => setShowAdd(false)} style={btn('#64748b')}>Abbrechen</button>
          </div>
        </form>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {['all','unbestaetigt','bestaetigt'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ ...btn(filter === f ? '#1e293b' : '#e2e8f0'), color: filter === f ? 'white' : '#1e293b', fontWeight: 700 }}>
            {f === 'all' ? 'Alle' : f === 'unbestaetigt' ? 'Kandidaten' : 'Bestätigt'}
          </button>
        ))}
      </div>

      {loading && <div style={{ color: '#64748b', fontSize: 13 }}>Lade...</div>}

      {!loading && items.length === 0 && (
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 24, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
          Keine wiederkehrenden Zahlungen gefunden. Klicke auf "Erkennen" um Kandidaten zu suchen.
        </div>
      )}

      {items.map(item => (
        <div key={item.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 8, borderLeft: `4px solid ${TYP_COLOR[item.typ] || '#94a3b8'}` }}>
          {editId === item.id ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div><label style={{ fontSize: 11, color: '#64748b' }}>Bezeichnung</label><input style={inp} value={editData.bezeichnung} onChange={e => setEditData({...editData, bezeichnung: e.target.value})} /></div>
                <div><label style={{ fontSize: 11, color: '#64748b' }}>Empfänger</label><input style={inp} value={editData.empfaenger} onChange={e => setEditData({...editData, empfaenger: e.target.value})} /></div>
                <div><label style={{ fontSize: 11, color: '#64748b' }}>Typ</label>
                  <select style={inp} value={editData.typ} onChange={e => setEditData({...editData, typ: e.target.value})}>
                    {Object.entries(TYP_LABEL).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div><label style={{ fontSize: 11, color: '#64748b' }}>Intervall</label>
                  <select style={inp} value={editData.intervall} onChange={e => setEditData({...editData, intervall: e.target.value})}>
                    {Object.entries(INTERVALL_LABEL).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div><label style={{ fontSize: 11, color: '#64748b' }}>Betrag</label><input style={inp} type="number" step="0.01" value={editData.erwarteter_betrag} onChange={e => setEditData({...editData, erwarteter_betrag: e.target.value})} /></div>
                <div><label style={{ fontSize: 11, color: '#64748b' }}>Nächstes Datum</label><input style={inp} type="date" value={editData.naechstes_datum} onChange={e => setEditData({...editData, naechstes_datum: e.target.value})} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={saveEdit} style={btn('#0a6f4f')}>Speichern</button>
                <button onClick={() => setEditId(null)} style={btn('#64748b')}>Abbrechen</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{item.bezeichnung}</span>
                    <ConfBadge c={item.confidence} />
                    {item.bestaetigt ? <span style={{ background: '#dff3e9', color: '#0a6f4f', fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>BESTÄTIGT</span>
                      : <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>KANDIDAT</span>}
                    {!item.aktiv && <span style={{ background: '#f1f5f9', color: '#64748b', fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>INAKTIV</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    <span style={{ color: TYP_COLOR[item.typ] || '#94a3b8', fontWeight: 700 }}>{TYP_LABEL[item.typ]}</span>
                    {' · '}{INTERVALL_LABEL[item.intervall]}
                    {' · '}{item.kategorie}
                    {item.naechstes_datum && <span> · Nächstes: <strong style={{ color: '#1e293b' }}>{item.naechstes_datum}</strong></span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right', minWidth: 90 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: (item.erwarteter_betrag || 0) < 0 ? '#ef4444' : '#10b981' }}>
                    {(item.betragstoleranz || 0) > 0.15 ? 'ca. ' : ''}
                    {item.erwarteter_betrag != null ? euro(item.erwarteter_betrag) : '—'}
                  </div>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                    {item.automatisch_erkannt ? 'auto' : 'manuell'}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {!item.bestaetigt && item.aktiv ? (
                  <button onClick={() => confirm(item)} style={btn('#0a6f4f')}>Bestätigen</button>
                ) : null}
                <button onClick={() => startEdit(item)} style={btn('#6366f1')}>Bearbeiten</button>
                {item.aktiv ? (
                  <button onClick={() => ignore(item)} style={btn('#f59e0b')}>Ignorieren</button>
                ) : null}
                <button onClick={() => del(item)} style={btn('#ef4444')}>Löschen</button>
                {item.kategorie && (
                  <button onClick={() => nav(`/buchungen?q=${encodeURIComponent(item.empfaenger || item.bezeichnung)}`)}
                    style={btn('#64748b')}>Buchungen</button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
